import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/core/services/api.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-material-consumption',
  templateUrl: './material-consumption.component.html',
  styleUrls: ['./material-consumption.component.scss'],
  standalone: false
})
export class MaterialConsumptionComponent implements OnInit {

  // Breadcrumb
  breadCrumbItems!: Array<{}>;
  
  // Form
  consumptionForm!: UntypedFormGroup;
  submitted = false;
  isEditMode = false;
  editId: any = null;
  showForm = false; // Toggle form visibility
  
  // Data
  consumptions: any[] = [];
  items: any[] = [];
  isLoading = false;
  totalItems = 0;
  
  // Pagination
  page: number = 1;
  pageSize: number = 10;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService
  ) { }

  ngOnInit(): void {
    // Breadcrumb
    this.breadCrumbItems = [
      { label: 'Stock Management' },
      { label: 'Material Consumption', active: true }
    ];

    // Initialize Form
    this.initializeForm();

    // Load data
    this.loadConsumptions();
    this.loadItems();
  }

  /**
   * Initialize Form
   */
  initializeForm(): void {
    this.consumptionForm = this.formBuilder.group({
      item_id: ['', [Validators.required]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      purpose: ['', [Validators.required]],
      remarks: ['']
    });
  }

  /**
   * Get form controls
   */
  get f() {
    return this.consumptionForm.controls;
  }

  /**
   * Load Material Consumptions
   */
  loadConsumptions(): void {
    this.isLoading = true;
    
    this.apiService.get(`/material_handovers?page=${this.page}&limit=${this.pageSize}`).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        
        if (response.success) {
          this.consumptions = response.data || [];
          this.totalItems = Number(response.total) || 0;
          this.pageSize = Number(response.perPage) || this.pageSize;
          this.page = Number(response.currentPage) || this.page;
        } else {
          this.consumptions = [];
          this.totalItems = 0;
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading material consumptions:', error);
        Swal.fire('Error', 'Failed to load material consumptions', 'error');
      }
    });
  }

  /**
   * Load Items
   */
  loadItems(): void {
    this.apiService.get('/items').subscribe({
      next: (response: any) => {
        if (response.success) {
          this.items = response.data || [];
        } else {
          this.items = [];
        }
      },
      error: (error) => {
        console.error('Error loading items:', error);
        this.items = [];
      }
    });
  }


  /**
   * Toggle form visibility
   */
  toggleForm(): void {
    this.showForm = !this.showForm;
    if (!this.showForm) this.resetForm();
  }

  /**
   * Reset form
   */
  resetForm(): void {
    this.submitted = false;
    this.isEditMode = false;
    this.editId = null;
    this.consumptionForm.reset({
      item_id: '',
      quantity: 1,
      purpose: '',
      remarks: ''
    });
  }

  /**
   * Edit Material Consumption
   */
  editConsumption(consumption: any): void {
    this.showForm = true;
    this.isEditMode = true;
    this.editId = consumption.id;
    
    this.consumptionForm.patchValue({
      item_id: consumption.itemId || consumption.item_id,
      quantity: consumption.quantity,
      purpose: consumption.purpose,
      remarks: consumption.remarks
    });
  }

  /**
   * Save Material Consumption
   */
  saveConsumption(): void {
    this.submitted = true;

    if (this.consumptionForm.invalid) return;

    const payload = this.consumptionForm.value;

    const apiCall = this.isEditMode
      ? this.apiService.put(`/material_handovers/${this.editId}`, payload)
      : this.apiService.post('/material_handovers', payload);

    apiCall.subscribe({
      next: (response: any) => {
        if (response.success) {
          Swal.fire('Success', this.isEditMode ? response.message : response.message, 'success');
          this.toggleForm();
          this.loadConsumptions();
        }
      }
    });
  }


  /**
   * Delete Material Consumption
   */
  deleteConsumption(id: number): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this consumption!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    }).then(result => {
      if (result.isConfirmed) {
        this.apiService.delete(`/material_handovers/${id}`).subscribe({
          next: (response: any) => {
            if (response.success) {
              Swal.fire('Deleted!', response.message, 'success');
              this.loadConsumptions();
            }
          }
        });
      }
    });
  }


  /**
   * Change page
   */
  changePage(page: number): void {
    this.page = page;
    this.loadConsumptions();
  }

  getMin(a: number, b: number): number {
    return Math.min(a, b);
  }
}
